import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import Card from '../Card';
import Loading from '../Loading';
import Alert from '../Alert';
import SynthesisCalendar from './SynthesisCalendar';
import DayDecisionList from './DayDecisionList';
import api from '../../services/api';

function toDateKey(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(dateKey) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00`);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function RuleSynthesisTab() {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed

  const [daySummariesByDate, setDaySummariesByDate] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState(null);

  useEffect(() => {
    loadMonth(year, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const loadMonth = async (y, m) => {
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      const startDate = toDateKey(new Date(y, m, 1));
      const endDate = toDateKey(new Date(y, m + 1, 0));
      const data = await api.getBatchDays(startDate, endDate);
      const byDate = {};
      (data.days || []).forEach((day) => {
        byDate[day.date] = day;
      });
      setDaySummariesByDate(byDate);
    } catch (err) {
      setCalendarError(err.message);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDayClick = async (dateKey) => {
    setSelectedDate(dateKey);
    setDayLoading(true);
    setDayError(null);
    setDayDetail(null);
    try {
      const data = await api.getDayDetail(dateKey);
      setDayDetail(data);
    } catch (err) {
      setDayError(err.message);
    } finally {
      setDayLoading(false);
    }
  };

  const handlePrevMonth = () => {
    setSelectedDate(null);
    setDayDetail(null);
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedDate(null);
    setDayDetail(null);
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else {
      setMonth((m) => m + 1);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <Card>
          {calendarLoading ? (
            <Loading message="Loading batch days..." />
          ) : calendarError ? (
            <Alert variant="error">Error loading batch days: {calendarError}</Alert>
          ) : (
            <SynthesisCalendar
              year={year}
              month={month}
              daySummariesByDate={daySummariesByDate}
              selectedDate={selectedDate}
              onDayClick={handleDayClick}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
            />
          )}
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Card>
          {!selectedDate ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Select a day with decisions to see the evidence chain.
              </Typography>
            </Box>
          ) : dayLoading ? (
            <Loading message="Loading day detail..." />
          ) : dayError ? (
            <Alert variant="error">Error loading day detail: {dayError}</Alert>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 2 }}>
                {formatDisplayDate(selectedDate)}
              </Typography>
              <DayDecisionList decisions={dayDetail?.decisions || []} />
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

export default RuleSynthesisTab;
